import { Entity } from '@brochington/ecstatic';
import { useAuth } from 'auth';
import {
  MapOverlayMachineEventsEnum,
  useMapOverlayMachine,
} from 'components/Overlays/MapOverlayMachine';
import AppStoreContext from 'contexts/AppStoreContext';
import ECSWorldContext from 'contexts/ECSWorldContext';
import { Components } from 'editor/components';
import Events from 'editor/components/Events';
import MapSheetEvents from 'editor/components/map/MapboxEvents/MapSheetEvents';
import Markup from 'editor/components/Markup';
import Immutable from 'immutable';
import { FC, useContext, useEffect } from 'react';
import { CreatePunch } from 'records/interactions/CreatePunch';
import { UpdatePunch } from 'records/interactions/UpdatePunch';
import { ViewRFI } from 'records/interactions/ViewRFI';
import { Markup as MarkupRecord } from 'records/Markup';
import { NewMarkup } from 'records/NewMarkup';
import { NewPunch } from 'records/NewPunch';
import { Space, SpaceId } from 'records/Space';
import { v4 as uuidv4 } from 'uuid';

type Props = {
  sheetLayerEntity: Entity<Components>;
};

const ECSMapSheetEvents: FC<Props> = (props) => {
  const { userId } = useAuth();
  const { sheetLayerEntity } = props;
  const world = useContext(ECSWorldContext);
  const appStore = useContext(AppStoreContext);
  const { mapOverlayMachineService } = useMapOverlayMachine();
  const mapSheetEventsComp =
    sheetLayerEntity.get<Events<MapSheetEvents>>(Events);

  useEffect(() => {
    mapSheetEventsComp.events.on('markup:click', ({ markup }) => {
      const { actions, state: appState } = appStore;
      const { rfis, punches } = appState;

      const rfi = rfis.find((r) => r.postedmarkupids.contains(markup.markupid));

      if (rfi) {
        const hasRfiPostInteraction = appState.hasInteraction('PostRFI');

        actions.deleteInteractions('ViewRFI');
        actions.deleteInteractions('UpdatePunch');

        if (!hasRfiPostInteraction) {
          const viewRFIInteraction = new ViewRFI({
            rfi,
            markup,
            canExpand: false,
            backArrow: false,
            placing: true,
            containerStyles: {
              top: 96,
              height: `calc(100vh - 96px)`,
            },
          });

          mapOverlayMachineService.send({
            type: MapOverlayMachineEventsEnum.OPEN_RFI_OVERLAY,
            data: {
              interaction: viewRFIInteraction,
            },
          });
        }
      }

      const punch = punches.find((p) => p.markupids.contains(markup.markupid));

      if (punch) {
        const updatePunch = new UpdatePunch({
          viewerOverlay: true,
          punch,
          containerStyles: {
            top: 96,
            height: `calc(100vh - 96px)`,
          },
        });

        // NOTE: This is super brute force, and doesn't account for
        //       handling unsaved changes. Needs to be better.
        mapOverlayMachineService.send({
          type: MapOverlayMachineEventsEnum.OPEN_UPDATE_PUNCH_OVERLAY,
          data: {
            interaction: updatePunch,
          },
        });
      }
    });

    mapSheetEventsComp.events.on(
      'markup:move',
      ({ markup }: { markup: MarkupRecord }) => {
        const { actions, state: appState } = appStore;

        const rfiPostInteraction = appState.getInteraction('PostRFI');

        if (rfiPostInteraction) {
          // @ts-ignore
          // TODO: work on event callback types
          const nextInteraction = rfiPostInteraction.set('markup', markup);

          actions.updateInteraction(nextInteraction);
        }

        // If the markup being moved is a new punch markup, then the interaction's punch
        // also needs to be updated to contain the correct markup so when it's saved, all
        // moves are also saved.
        // When creating a punch, it should only be possible to move the punch currently
        // being created, so no "new punch" check is needed.
        const createPunchInteraction = appState.getInteraction('CreatePunch');
        if (createPunchInteraction) {
          const updatedInteraction = createPunchInteraction.setIn(
            ['punch', 'markups'],
            Immutable.List([markup.intoNewMarkup()])
          );

          actions.updateInteraction(updatedInteraction);
        }

        // markup is a punch markup
        const isPunch = appState.punches.some((p) =>
          p.markupids.contains(markup.markupid)
        );

        if (isPunch) {
          const role = appState.getUserRole(userId);
          if (!['admin', 'member'].includes(role)) {
            return; // make sure the local markup is not changed.
          }

          actions.updateMarkup(markup);
        }

        // Need to make sure that the local copies of markups in AppState are updated
        // to reflect what is displayed by the viewer, since visual updates to markups
        // are dependent on appState changing. Basically, if the appState markups change,
        // then the markups on the map should follow. This logic should be handled in
        // ECSSheetMarkup.tsx.
        actions.setLocalMarkup(markup);
      }
    );

    mapSheetEventsComp.events.on('postRFI:create', async ({ markup }) => {
      const { state: appState, actions } = appStore;

      const postRFI = appState.getInteraction('PostRFI');
      if (!postRFI) return; // an RFIPost interaction should exist

      markup.selected = true;

      const interactionWithMarkup = postRFI.set('markup', markup.markup);

      await actions.setLocalMarkup(markup.markup);
      await actions.updateInteraction(interactionWithMarkup);

      // Make sure that the newly posted RFI is selected in the editor.
      world.grabAll(Markup).forEach(({ component }) => {
        if (component.markup.markupid === markup.markup.markupid) {
          component.selected = true;
        }
      });
    });

    mapSheetEventsComp.events.on('punch:create', async ({ markup, pageid }) => {
      const { state: appState, actions } = appStore;
      const newMarkup = new NewMarkup({
        clientGeneratedID: uuidv4(),
        fileid: pageid,
        projectid: appState.selectedProject,
        jsonBlob: JSON.stringify(markup?.getAsObject()),
      });

      const [b, l, t, r] = markup?.getRect() as number[];
      const cx = b + (t - b) / 2;
      const cy = l + (r - l) / 2;
      const space = appState.spaces
        .get(pageid, Immutable.OrderedMap<SpaceId, Space>())
        .valueSeq()
        .find((s) => s.pointInSpace(cx, cy));
      const createPunchInteraction = new CreatePunch({
        viewerOverlay: true,
        containerStyles: {
          top: 96,
          height: `calc(100vh - 96px)`,
        },
        punch: new NewPunch({
          location: space ? space.title : '',
          projectid: appStore.state.selectedProject,
          clientcreateddate: Math.floor(Date.now() / 1000),
          markups: Immutable.List([newMarkup]),
        }),
      });

      world.grabAll(Markup).forEach(({ component }) => {
        if (component.markup.markupid === markup.markupid) {
          component.selected = true;
        }
      });
      actions.setLocalMarkup(newMarkup.intoMarkup());
      actions.deleteInteractions('PlacePunch');
      mapOverlayMachineService.send({
        type: MapOverlayMachineEventsEnum.OPEN_CREATE_PUNCH_OVERLAY,
        data: { interaction: createPunchInteraction },
      });
    });

    mapSheetEventsComp.events.on('markups:deselect-all', () => {
      mapOverlayMachineService.send(MapOverlayMachineEventsEnum.CLOSE_OVERLAY);
    });

    mapSheetEventsComp.events.on('place-punch:cancel', () => {
      mapOverlayMachineService.send(MapOverlayMachineEventsEnum.CLOSE_OVERLAY);

      appStore.actions.deleteInteractions('PlacePunch');
    });

    return () => {
      mapSheetEventsComp.onEventsRemove(world);
    };
  }, []);

  return null;
};

export default ECSMapSheetEvents;
